import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect, useRef } from "react";

import API_Calls_Dashboard from "@/api/calls/API_Calls_Dashboard";
import useGetCachesHandler from "@/hooks/useGetCachesHandler";

export function useCatalistExport({ handleCustomFields }) {
  const { user } = useAuth0();
  const { loadDefaultCatalistFields } = useGetCachesHandler();
  const { setCatalistFieldPreferences } = API_Calls_Dashboard();
  // Intentionally creating a distinction between Fields and Settings in these variable names.
  // Fields are only the categories/fields
  // Settings includes any additional settings which would be sent as query parameters in the url.
  const [defaultFields, setDefaultFields] = useState(null); // fields and values from source-of-truth default field route (all fields set to true)
  const [fetchedSettings, setFetchedSettings] = useState(null); // settings that have been fetched from preferences.yaml, including params
  const [open, setOpen] = useState(false);
  const [notification, setNotification] = useState({});
  const [expandedPanels, setExpandedPanels] = useState({});
  const summaryRefs = useRef({});
  const [checkedFields, setCheckedFields] = useState([]); // array of field name strings ["dwid", "firstname", ...]
  const [checkedCategories, setCheckedCategories] = useState([]);
  const [format, setFormat] = useState("csv");

  useEffect(() => {
    const initializeData = async () => {
      try {
        // fetch both default and preferred settings in parallel
        const [defaultRes, preferredRes] = await Promise.all([
          loadDefaultCatalistFields(),
          setCatalistFieldPreferences(user?.org_name),
        ]);

        // process default catalist fields
        const { parameterSettings: _defaultParams, ...fields } =
          defaultRes || {};
        const transformedDefaultFields = {};
        // adding the array of arrays to the key/value so the checkboxes in the form can be populated
        for (const [key, value] of Object.entries(fields)) {
          const newFields = value.map((field) => [field, true]);
          transformedDefaultFields[key] = newFields;
        }
        setDefaultFields(transformedDefaultFields);
        let finalFields = transformedDefaultFields; // fallback to defaults

        // process preferred catalist fields (if they exist)
        if (preferredRes?.data?.preferred_catalist_fields) {
          const { parameterSettings: params, ...catalistFields } =
            preferredRes.data.preferred_catalist_fields;

          setFormat(params?.format || "csv");

          const transformedCatalistFields = Object.fromEntries(
            Object.entries(catalistFields).map(([category, innerObject]) => [
              category,
              Object.entries(innerObject),
            ])
          );

          setFetchedSettings({
            parameterSettings: { format: params?.format || "csv" },
            ...transformedCatalistFields,
          });

          finalFields = transformedCatalistFields;
        }

        populateCheckboxes(finalFields);
      } catch (error) {
        console.error("Failed to initialize Catalist export fields:", error);
      }
    };

    initializeData();
    // loadDefaultCatalistFields and setCatalistFieldPreferences are recreated
    // by their hooks every render; intentionally fire only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSavePreferences = async () => {
    const newPreferredFields = {};
    Object.entries(defaultFields).forEach(([categoryKey, fields]) => {
      const newArray = fields.map((field) => [
        field[0],
        checkedFields.includes(field[0]),
      ]);
      newPreferredFields[categoryKey] = newArray;
    });
    const newSetting = { ...newPreferredFields, parameterSettings: { format } };
    setFetchedSettings(newSetting);
    // transform preference data for nice storage in yaml file
    const transformedSettings = Object.fromEntries(
      Object.entries(defaultFields).map(([category, arrayOfArrays]) => {
        const newFieldPairs = arrayOfArrays.map(([field]) => [
          field,
          checkedFields.includes(field),
        ]);
        return [category, Object.fromEntries(newFieldPairs)];
      })
    );
    transformedSettings["parameterSettings"] = { format };

    const res = await setCatalistFieldPreferences(
      user?.org_name,
      transformedSettings
    );
    if (res?.error) {
      setNotification({
        message: "Unable to set preferences at this time",
        severity: "error",
        duration: 7000,
      });
    } else {
      setNotification({
        message: "Preferences set successfully",
        severity: "success",
        duration: 7000,
      });
    }
  };

  const handleRestorePreferences = () => {
    if (!fetchedSettings) {
      // no saved preferences in yaml
      const { parameterSettings: _parameterSettings, ...defaultFields } =
        defaultFields;
      populateCheckboxes(defaultFields);
    } else {
      const { parameterSettings: params, ...fetchedFields } = fetchedSettings;
      setFormat(params.format);
      populateCheckboxes(fetchedFields);
    }
  };

  const populateCheckboxes = (fields) => {
    const preferredCategories = [];
    const preferredFields = [];
    Object.entries(fields).forEach(([categoryKey, fieldsList]) => {
      preferredCategories.push(categoryKey);
      let uncheckedField = false;
      fieldsList.forEach((field) => {
        if (field[1]) {
          preferredFields.push(field[0]);
        } else {
          uncheckedField = true;
        }
      });
      if (uncheckedField) {
        preferredCategories.pop();
      }
    });
    setCheckedCategories(preferredCategories);
    setCheckedFields(preferredFields);
  };

  const handleCheckAllFields = () => {
    populateCheckboxes(defaultFields);
  };

  const confirmFields = () => {
    handleCustomFields({
      fields: checkedFields,
      format,
    });
    handleClose();
  };

  const handleCategoryCheckboxClick = (categoryKey) => (event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setCheckedCategories((prev) => [...prev, categoryKey]);
      const fieldsToAdd = (defaultFields[categoryKey] || [])
        .filter((field) => !checkedFields.includes(field[0]))
        .map((field) => field[0]);
      setCheckedFields((prev) => [...prev, ...fieldsToAdd]);
    } else {
      setCheckedCategories((prev) => prev.filter((c) => c !== categoryKey));
      const fieldsToRemove = new Set(
        (defaultFields[categoryKey] || []).map((field) => field[0])
      );
      setCheckedFields((prev) => prev.filter((f) => !fieldsToRemove.has(f)));
    }
  };

  const handleCheckboxChange = (changedField, categoryKey) => (event) => {
    if (event.target.checked) {
      setCheckedFields((prev) => [...prev, changedField]);
    } else {
      setCheckedFields((prev) => prev.filter((f) => f !== changedField));
      setCheckedCategories((prev) => prev.filter((c) => c !== categoryKey));
    }
  };

  const handleFormatChange = (e) => setFormat(e.target.value);

  const handleIconClick = (id) => (event) => {
    event.stopPropagation();
    setExpandedPanels((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return {
    defaultFields,
    open,
    notification,
    setNotification,
    expandedPanels,
    summaryRefs,
    checkedFields,
    checkedCategories,
    format,
    handleClickOpen,
    handleClose,
    handleSavePreferences,
    handleRestorePreferences,
    handleCheckAllFields,
    confirmFields,
    handleCategoryCheckboxClick,
    handleCheckboxChange,
    handleFormatChange,
    handleIconClick,
  };
}
